import {
  convertToDataTableColumns,
  type ReportDataResponse,
  type ReportRow,
  type ReportTableColumn,
} from '@app/+authenticated/+reports/shared/api/ReportApi';
import { type ReportFilterValues } from '@app/+authenticated/+reports/shared/report-filters/report-filter.interfaces';
import { reportOptions, ReportType } from '@reducers/orm/reports/report.model';
import { hasAllPermissions, type PermissionState } from '@sb/shared/permission/util';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';

import { type PayrollRunScope } from './payroll.model';

/**
 * The run's departments narrowed to those the payroll report may actually be requested for.
 *
 * The flow's own department picker is an **any-of** gate over the four payroll permissions
 * (`payrollDepartmentPermissionCheck`), because a manager needs only one actionable step to enter
 * the flow. The payroll report is stricter: it requires **all** of
 * `reportOptions[ReportType.PAYROLL].permissions`, and the reports UI narrows its own department
 * options the same way (`getReportableDepartments` -> `hasAllPermissions`).
 *
 * Without this, a scope spanning a department the manager can only approve timesheets for is sent
 * verbatim. The backend intersects it away (`Access::checkDepartmentsForAllPermissions`), so no
 * data leaks, but the report then silently covers fewer departments than the run says it does,
 * and a scope with no qualifying department at all is rejected outright as a 403.
 *
 * Derived from `reportOptions` rather than restating the two permissions, so this cannot drift
 * from what the report and the backend actually require.
 */
export function reportableDepartmentIds(departmentIds: string[], permissionState: PermissionState): string[] {
  return departmentIds.filter((departmentId) =>
    hasAllPermissions(
      {
        userId: 'me',
        permissions: reportOptions[ReportType.PAYROLL].permissions,
        departments: departmentId,
      },
      permissionState,
    ),
  );
}

/**
 * The run's scope as payroll report filters, in the web format `ReportService.triggerReport`
 * converts before sending: `PlainDateRange`s, which is what the report's own `sb-date-range-picker`
 * controls bind and what `getDefaultFilterValue` seeds them with.
 *
 * The flow's single range feeds **both** period filters because the two select different employee
 * populations rather than different data for the same people: the backend takes `contractPeriod`
 * as the period for salaried staff and `workedPeriod` as the period for hourly staff
 * (`PayrollFactory::getPayrolls`, whose parameters are named `$fixedPayPeriod` and
 * `$hourlyPayPeriod`). Both are read unguarded there, so neither may be omitted.
 *
 * `departmentIds` is passed separately rather than read off `scope`, so the caller cannot skip
 * deciding which departments are reportable — see `reportableDepartmentIds`.
 *
 * `contractType` is left unset: scoping a run by contract type is out of the MVP, and the backend
 * defaults it to "no restriction".
 */
export function payrollReportFilters(scope: PayrollRunScope, departmentIds: string[]): ReportFilterValues {
  const from = toISODateString(scope.range.start);
  const to = toISODateString(scope.range.end);

  return {
    contractPeriod: { start: from, end: to },
    workedPeriod: { start: from, end: to },
    contractDepartment: [...departmentIds],
  };
}

/** A generated report's contents, in the shape `sb-report-table` binds. */
export interface PayrollReportData {
  columns: ReportTableColumn[];
  rows: ReportRow[];
}

/**
 * A fetched report as the step renders it.
 *
 * This is every part of `ReportsHelperService.processReportResponse` that applies outside the
 * reports page. The rest of that method is page state the flow has no business writing: it
 * assigns the helper's mutable `mode`/`isLoading`/`cardHeader` fields, and calls
 * `reportService.setFilters`, which pushes into the reports UI's own `updateFilters` stream.
 *
 * `columns` is not assumed to be present. `ReportApi.fetchReport` returns whatever the untyped
 * gateway gives it, so `ReportDataResponse` is a declared shape rather than a guarantee — the
 * same caveat `PayrollReportService` already documents for `ReportStatusModel.id`.
 */
export function payrollReportData(response: ReportDataResponse | undefined): PayrollReportData {
  return {
    columns: convertToDataTableColumns(response?.columns ?? []),
    rows: response?.rows ?? [],
  };
}
